"use client";

import { useMemo, useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Box, Typography } from "@mui/material";
import { Icon } from "@/components/icons";
import { APP_MAX_WIDTH } from "@/components/PageContent/PageContent";
import { SAFE_AREA_BOTTOM } from "@/contexts/SafeAreaContext";
import { Z_INDEX } from "@/constants/zIndex";
import { useDarkColors } from "@/hooks/useDarkColors";
import { getAssetUrl } from "@/utils/assetUrl";
import {
  TEMP_TEXT_BOLD_XXL,
  TEMP_TEXT_BOLD_XS,
  TEMP_TEXT_SEMIBOLD_XL,
  TEMP_TEXT_SEMIBOLD_L,
  TEMP_TEXT_MEDIUM_L,
  TEMP_TEXT_MEDIUM_XS,
} from "@/constants/tempTypo";
import type { WorldCupCharacter } from "@/stores/worldCupStore";
import { useAuthStore } from "@/stores/authStore";
import { useTranslator } from "@/lang";

interface CharacterRevealProps {
  character: WorldCupCharacter;
  greetingText?: string | null;
  greetingVoiceUrl?: string | null;
  onComplete: () => void;
  onSkip: () => void;
}

const EASING: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

export default function CharacterReveal({
  character,
  greetingText,
  greetingVoiceUrl,
  onComplete,
  onSkip,
}: CharacterRevealProps) {
  const c = useDarkColors();
  const { t, f } = useTranslator();

  const isAdultVerified = useAuthStore((s) => s.user?.isAdultVerified ?? false);
  const isSafetyMode = useAuthStore((s) => s.user?.isSafetyMode ?? true);
  // 성인인증 완료 + 세이프티모드 해제 유저에게만 첫 번째 성향 태그 노출
  const canRevealSexualTag = isAdultVerified && !isSafetyMode;

  const name = character.name?.ko || character.name?.en || "???";
  const jobText = character.job?.ko || character.job?.en || "";
  const ageText = character.age
    ? f(t("worldcup.age_format"), [character.age])
    : "";
  const infoText = [ageText, jobText].filter(Boolean).join(" · ");
  const profileUrl = getAssetUrl(character.profileImagePath, "large");

  const personalityTags: string[] = useMemo(() => {
    if (!character.personalityTags) return [];
    const raw = character.personalityTags;
    const tagSource = raw?.ko || raw?.en || raw;
    if (Array.isArray(tagSource)) return tagSource.slice(0, 3);
    return [];
  }, [character.personalityTags]);

  const sexualTags: string[] = useMemo(() => {
    if (!character.sexualTendencyTags) return [];
    const raw = character.sexualTendencyTags;
    const src = raw?.ko || raw?.en || raw;
    if (Array.isArray(src)) return src.slice(0, 3);
    return [];
  }, [character.sexualTendencyTags]);

  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlayVoice = useCallback(() => {
    if (!greetingVoiceUrl) return;
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      return;
    }
    const audio = new Audio(greetingVoiceUrl);
    audioRef.current = audio;
    audio.play();
    setIsPlaying(true);
    audio.onended = () => setIsPlaying(false);
  }, [greetingVoiceUrl, isPlaying]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: Z_INDEX.MODAL,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.5)",
        }}
      />

      {/* Bottom sheet card */}
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "tween", duration: 0.3, ease: EASING }}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: APP_MAX_WIDTH,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            bgcolor: c.bg,
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
            overflow: "hidden",
          }}
        >
          {/* Handle */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: "12px",
              pb: "4px",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: "9999px",
                bgcolor: c.isDark ? "#555555" : "#D9D9D9",
              }}
            />
          </Box>

          {/* Content */}
          <Box
            sx={{
              px: "24px",
              pt: "12px",
              pb: `calc(20px + ${SAFE_AREA_BOTTOM})`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Subtitle */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <Typography
                sx={{
                  ...TEMP_TEXT_MEDIUM_L,
                  color: c.fgLow,
                  mb: "12px",
                }}
              >
                {t("worldcup.reveal_subtitle")}
              </Typography>
            </motion.div>

            {/* Profile image */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay: 0.1,
                duration: 0.5,
                type: "spring",
                stiffness: 200,
                damping: 18,
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  overflow: "hidden",
                  mb: "16px",
                  border: `3px solid ${c.primary}`,
                  boxShadow: c.isDark
                    ? "0 0 32px rgba(124, 58, 237, 0.3)"
                    : "0 0 32px rgba(75, 0, 204, 0.12)",
                }}
              >
                {profileUrl ? (
                  <Box
                    component="img"
                    src={profileUrl}
                    alt={name}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      bgcolor: c.bgInset,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 48 }}>👤</Typography>
                  </Box>
                )}
              </Box>
            </motion.div>

            {/* Name */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.4, ease: EASING }}
            >
              <Typography
                sx={{
                  ...TEMP_TEXT_BOLD_XXL,
                  color: c.fg,
                  mb: "4px",
                }}
              >
                {name}
              </Typography>
            </motion.div>

            {/* Info */}
            {infoText && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.45, duration: 0.4 }}
              >
                <Typography
                  sx={{
                    ...TEMP_TEXT_MEDIUM_L,
                    color: c.fgLower,
                    mb: "10px",
                  }}
                >
                  {infoText}
                </Typography>
              </motion.div>
            )}

            {/* MBTI */}
            {character.mbti && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.3 }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    px: "8px",
                    py: "3px",
                    borderRadius: "8px",
                    bgcolor: c.isDark
                      ? "rgba(124, 58, 237, 0.15)"
                      : "rgba(75, 0, 204, 0.06)",
                    mb: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      ...TEMP_TEXT_BOLD_XS,
                      color: c.primary,
                      letterSpacing: "0.5px",
                    }}
                  >
                    {character.mbti}
                  </Typography>
                </Box>
              </motion.div>
            )}

            {/* Personality + Sexual Tendency Tags */}
            {(personalityTags.length > 0 || sexualTags.length > 0) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.55, duration: 0.4 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "4px",
                    mb: "16px",
                  }}
                >
                  {personalityTags.map((tag, i) => (
                    <Box
                      key={`p-${i}`}
                      sx={{
                        px: "8px",
                        py: "3px",
                        borderRadius: "100px",
                        bgcolor: "rgba(120, 90, 220, 0.1)",
                      }}
                    >
                      <Typography
                        sx={{
                          ...TEMP_TEXT_BOLD_XS,
                          color: "rgba(120, 90, 220, 1)",
                        }}
                      >
                        {tag.startsWith("#") ? tag : `#${tag}`}
                      </Typography>
                    </Box>
                  ))}
                  {sexualTags.map((tag, i) => (
                    <Box
                      key={`s-${i}`}
                      sx={{
                        position: "relative",
                        px: "8px",
                        py: "3px",
                        borderRadius: "100px",
                        bgcolor: "rgba(220, 80, 120, 0.1)",
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        sx={{
                          ...TEMP_TEXT_BOLD_XS,
                          color: "rgba(220, 80, 120, 1)",
                          filter:
                            i > 0 || !canRevealSexualTag ? "blur(5px)" : "none",
                        }}
                      >
                        {tag.startsWith("#") ? tag : `#${tag}`}
                      </Typography>
                      {(i > 0 || !canRevealSexualTag) && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon
                            name="icLock"
                            size={10}
                            color="rgba(220, 80, 120, 0.7)"
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </motion.div>
            )}

            {/* Greeting speech bubble */}
            {greetingText && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.4, ease: EASING }}
                style={{ width: "100%" }}
              >
                <Box
                  sx={{
                    position: "relative",
                    bgcolor: c.bgInset,
                    border: `1px solid ${c.border}`,
                    borderRadius: "16px",
                    px: "16px",
                    py: "12px",
                    mb: "16px",
                  }}
                >
                  <Typography
                    sx={{
                      ...TEMP_TEXT_MEDIUM_XS,
                      color: c.fgLow,
                      lineHeight: 1.5,
                      textAlign: "center",
                      fontStyle: "italic",
                    }}
                  >
                    {greetingText.replace(/\[.*?\]/g, "").trim()}
                  </Typography>
                  {greetingVoiceUrl && (
                    <Box
                      onClick={handlePlayVoice}
                      sx={{
                        position: "absolute",
                        top: -12,
                        right: 12,
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: c.primary,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      }}
                    >
                      <Icon
                        name={isPlaying ? "icPauseFill" : "icPlayFill"}
                        size={14}
                        color="#FFFFFF"
                      />
                    </Box>
                  )}
                </Box>
              </motion.div>
            )}

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.4, ease: EASING }}
              style={{ width: "100%" }}
            >
              <Box
                onClick={onComplete}
                sx={{
                  width: "100%",
                  height: 56,
                  borderRadius: "16px",
                  bgcolor: c.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                  mb: "10px",
                }}
              >
                <Typography
                  sx={{
                    ...TEMP_TEXT_SEMIBOLD_XL,
                    color: "#FFFFFF",
                  }}
                >
                  {t("worldcup.reveal_start_chat")}
                </Typography>
              </Box>

              <Box
                onClick={onSkip}
                sx={{
                  width: "100%",
                  height: 48,
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: c.fgLower }}>
                  {t("worldcup.reveal_skip")}
                </Typography>
              </Box>
            </motion.div>
          </Box>
        </Box>
      </motion.div>
    </motion.div>
  );
}
